import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { title: "", image_url: "", category: "Kegiatan", media_type: "image" };
const CATS = ["Touring", "Kopdar", "Modifikasi", "Baksos", "Kegiatan"];
const inputCls = "border-white/10 bg-[#0B0D15] text-slate-200 placeholder:text-slate-500";

export const GalleryTab = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (g) => {
    setEditing(g);
    setForm({ title: g.title, image_url: g.image_url, category: g.category, media_type: g.media_type || "image" });
    setOpen(true);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      toast.error("Ukuran file melebihi 50 MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);

    try {
      const res = await api.post("/admin/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const isVideo = file.type.startsWith("video") || [".mp4", ".webm", ".mov", ".avi", ".mkv"].some((ext) => file.name.toLowerCase().endsWith(ext));
      const mediaType = res.data.media_type || (isVideo ? "video" : "image");

      let autoTitle = form.title;
      if (!autoTitle.trim()) {
        autoTitle = file.name.replace(/\.[^/.]+$/, "").replace(/[-_]/g, " ");
      }

      setForm((prev) => ({
        ...prev,
        image_url: res.data.url,
        media_type: mediaType,
        title: autoTitle,
      }));

      toast.success("File berhasil diunggah");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/gallery/${editing.id}`, form);
      else await api.post("/admin/gallery", form);
      toast.success(editing ? "Foto diperbarui" : "Foto ditambahkan");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Hapus foto "${g.title}"?`)) return;
    await api.delete(`/admin/gallery/${g.id}`);
    toast.success("Foto dihapus");
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-gallery-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold uppercase text-white">Kelola Galeri</h2>
        <Button data-testid="admin-add-gallery-button" onClick={openNew} className="bg-rose-600 text-white hover:bg-rose-500">
          <Plus className="mr-2 h-4 w-4" /> Tambah Foto
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {items.map((g) => (
          <div key={g.id} data-testid={`admin-gallery-item-${g.id}`}
            className="group relative h-44 overflow-hidden rounded-xl border border-white/10">
            {g.media_type === "video" ? (
              <video src={g.image_url} muted playsInline preload="metadata" className="h-full w-full object-cover" />
            ) : (
              <img src={g.image_url} alt={g.title} className="h-full w-full object-cover" />
            )}
            <div className="absolute inset-0 flex flex-col justify-between bg-gradient-to-t from-black/85 via-black/20 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
              <div className="flex justify-end gap-1">
                <Button data-testid={`admin-gallery-edit-${g.id}`} size="icon" variant="ghost" onClick={() => openEdit(g)}
                  className="h-8 w-8 bg-black/50 text-slate-200 hover:text-amber-400"><Pencil className="h-4 w-4" /></Button>
                <Button data-testid={`admin-gallery-delete-${g.id}`} size="icon" variant="ghost" onClick={() => remove(g)}
                  className="h-8 w-8 bg-black/50 text-slate-200 hover:text-rose-400"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div>
                <p className="font-heading text-base font-bold uppercase text-white">{g.title}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-rose-400">{g.category}</p>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-full py-8 text-center text-slate-500">Belum ada foto.</p>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg border-white/10 bg-[#11131F]" data-testid="admin-gallery-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold uppercase text-white">{editing ? "Edit Foto" : "Tambah Foto"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Judul Foto *</Label>
              <Input data-testid="gallery-form-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">File dari Perangkat</Label>
              <div
                onClick={() => !uploading && fileInputRef.current?.click()}
                className="group relative flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-amber-500/40 bg-[#0B0D15] p-4 text-center transition-all hover:border-amber-400 hover:bg-[#0E111C]"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {uploading ? (
                  <div className="flex flex-col items-center py-2 text-rose-400">
                    <Loader2 className="mb-2 h-6 w-6 animate-spin" />
                    <span className="text-sm font-medium">Mengunggah file...</span>
                  </div>
                ) : (
                  <>
                    <div className="mb-1.5 flex items-center justify-center gap-2 text-amber-400 group-hover:text-amber-300">
                      <Upload className="h-4 w-4" />
                      <span className="text-sm font-semibold">Upload Foto / Video dari Perangkat</span>
                    </div>
                    <p className="text-[11px] text-slate-400">
                      JPG, PNG, WebP, GIF, MP4, atau WebM — maksimal 50 MB.
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="relative my-0.5 text-center">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-white/10" />
              </div>
              <span className="relative bg-[#11131F] px-3 font-mono text-[11px] text-slate-500">
                atau tempel link
              </span>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">URL Gambar / Video *</Label>
              <Input data-testid="gallery-form-image-input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." className={inputCls} />
            </div>

            {form.image_url && form.media_type === "image" && <img src={form.image_url} alt="Pratinjau" className="h-40 w-full rounded-lg border border-white/10 object-cover" />}
            {form.image_url && form.media_type === "video" && <video src={form.image_url} controls className="h-40 w-full rounded-lg border border-white/10 object-cover" />}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="gallery-form-category-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#11131F] text-slate-200">
                    {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Jenis Media</Label>
                <Select value={form.media_type} onValueChange={(v) => setForm({ ...form, media_type: v })}>
                  <SelectTrigger data-testid="gallery-form-media-type-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#11131F] text-slate-200">
                    <SelectItem value="image">Foto</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="gallery-form-save-button" onClick={save} disabled={saving || uploading || !form.title.trim() || !form.image_url.trim()}
              className="bg-rose-600 font-bold text-white hover:bg-rose-500">
              {saving ? "Menyimpan..." : "Simpan Foto"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

